import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../firebase';
import logger from '../../utils/logger';
import {formatCurrency} from '../Help/helper';
import PaymentModal from './PaymentModal';
import { Button, Card, Badge, EmptyState, SkeletonTable } from '../UI';
import { EyeIcon } from '@heroicons/react/20/solid';

const Receivable = ({ invoice }) => {
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const invoicesQuery = query(
          collection(db, 'invoices'),
          where('approvalStatus', '==', 'Yes'),
          where('receivable', '>', 0),
          orderBy('receivable'),
          orderBy('date')
        );
        const invoiceSnapshot = await getDocs(invoicesQuery);
        const invoiceData = invoiceSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
        setInvoices(invoiceData);
      } catch (error) {
        logger.error('Error fetching receivables:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <Card className="mb-6">
        <div className="px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Accounts Receivable</h1>
          <p className="text-sm text-gray-600">Track outstanding payments and collect revenue</p>
        </div>
      </Card>

      <Card>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-6 bg-gray-50 border-b border-gray-200">
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <p className="text-sm text-gray-600 mb-1">Total Receivables</p>
            <p className="text-2xl font-bold text-primary-600">
              {formatCurrency(invoices.reduce((sum, inv) => sum + (inv.receivable || 0), 0))}
            </p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <p className="text-sm text-gray-600 mb-1">Outstanding Invoices</p>
            <p className="text-2xl font-bold text-warning-600">{invoices.length}</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <p className="text-sm text-gray-600 mb-1">Average Amount</p>
            <p className="text-2xl font-bold text-gray-900">
              {invoices.length > 0
                ? formatCurrency(invoices.reduce((sum, inv) => sum + (inv.receivable || 0), 0) / invoices.length)
                : formatCurrency(0)}
            </p>
          </div>
        </div>

        {/* Table Section */}
        <div className="overflow-x-auto">
          {loading ? (
            <SkeletonTable rows={8} columns={5} />
          ) : invoices.length === 0 ? (
            <EmptyState
              title="No receivables found"
              message="All invoices have been paid. Great work!"
              icon={
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
            />
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Invoice Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Approval Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Amount Due
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-primary-600">{invoice.invoiceNumber}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{invoice.customerName}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {invoice.approvalDate?.toDate().toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-semibold text-gray-900">
                        {formatCurrency(invoice.receivable)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Badge variant="danger">Outstanding</Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <Button
                        onClick={() => setSelectedInvoice(invoice)}
                        variant="primary"
                        size="sm"
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        View Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      {selectedInvoice && (
        <PaymentModal
          invoice={selectedInvoice}
          isOpen={!!selectedInvoice}
          onRequestClose={() => setSelectedInvoice(null)}
        />
      )}
    </div>
  );
};

export default Receivable;
